import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack, keyframes } from '@mui/material';
import { debounce } from 'lodash';
import img from './logobg.png';

// Float animation
const float = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(20px); }
  100% { transform: translateY(0); }
`;
const float2 = keyframes`
  0% { transform: translateX(0); }
  50% { transform: translateX(25px); }
  100% { transform: translateX(0); }
`;
// Fade-in
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Section7 = () => {
  const [clickedItem, setClickedItem] = useState(null);

  const handleScroll = debounce((sectionId, retries = 5, interval = 500) => {
    const tryScroll = (attemptsLeft) => {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = (document.querySelector('header')?.offsetHeight || 80) + 20;
        const y = element.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: y, behavior: 'smooth' });
        setClickedItem(sectionId);
      } else if (attemptsLeft > 0) {
        setTimeout(() => tryScroll(attemptsLeft - 1), interval);
      }
    };
    tryScroll(retries);
  }, 400);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const intersecting = entries.find((entry) => entry.isIntersecting);
        setClickedItem(intersecting?.target.id || null);
      },
      { threshold: 0.6, rootMargin: '-10% 0px -10% 0px' }
    );

    const observeSections = () => {
      ['what_we_do', 'our_vision', 'our_mission', 'our_team'].forEach((id) => {
        const element = document.getElementById(id);
        if (element) observer.observe(element);
      });
    };

    observeSections();
    const interval = setInterval(observeSections, 1000);
    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, []);

  return (
    <Box
      component="section"
      aria-label="About Vertex Suite"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 2, sm: 3, md: 3, lg: 3.5 }, // Reduced padding for balanced spacing
        px: { xs: 2, sm: 3, md: 4, lg: 6 }, // Adjusted horizontal padding
        background: 'linear-gradient(135deg, #E6F1FB 0%, #F4F9FF 100%)',
        backgroundSize: '400% 400%',
        animation: `${fadeIn} 1.2s ease-in`,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Floating blobs */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: '-50px', sm: '-80px', md: '-100px' },
          left: { xs: '-50px', sm: '-80px', md: '-100px' },
          width: { xs: '200px', sm: '300px', md: '400px' },
          height: { xs: '200px', sm: '300px', md: '400px' },
          background: 'radial-gradient(circle, #1F85C0 0%, transparent 70%)',
          borderRadius: '50%',
          animation: `${float} 8s ease-in-out infinite`,
          opacity: 0.3,
          zIndex: 0,
          filter: 'blur(40px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: '-40px', sm: '-60px', md: '-80px' },
          right: { xs: '-40px', sm: '-60px', md: '-100px' },
          width: { xs: '150px', sm: '200px', md: '300px' },
          height: { xs: '150px', sm: '200px', md: '300px' },
          background: 'radial-gradient(circle, #054E91 0%, transparent 70%)',
          borderRadius: '50%',
          animation: `${float2} 10s ease-in-out infinite`,
          opacity: 0.2,
          zIndex: 0,
          filter: 'blur(30px)',
        }}
      />

      {/* Main content */}
      <Stack
        component="article"
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 3, sm: 4, md: 5 }}
        alignItems="center"
        justifyContent="center"
        sx={{
          position: 'relative',
          zIndex: 1,
          maxWidth: { xs: '100%', sm: '90%', md: '1280px' },
          mx: 'auto',
          minHeight: { xs: 'auto', md: '400px' }, // Adjusted for smaller screens
        }}
      >
        <Box
          sx={{
            flex: 1,
            textAlign: 'center',
            animation: `${fadeIn} 1s ease-in forwards`,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <Box
            component="img"
            src={img}
            alt="Vertex Suite Logo - Business Messaging Platform"
            sx={{
              width: {
                xs: '90%',
                sm: '80%',
                md: '90%',
                lg: '100%',
              },
              maxWidth: {
                xs: '280px',
                sm: '350px',
                md: '450px',
                lg: '550px',
              },
              height: 'auto',
              mx: 'auto',
              display: 'block',
            }}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            animation: `${fadeIn} 1.3s ease-in forwards`,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            px: { xs: 1, sm: 0 },
          }}
        >
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '22px', sm: '28px', md: '34px' },
              mb: { xs: 1.5, sm: 1 },
              color: '#054E91',
              fontFamily: "'Roboto', sans-serif",
              textAlign: 'left',
            }}
          >
            Vertex{' '}
            <Box component="span" sx={{ color: '#1F85C0' }}>
              Suite
            </Box>
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: '15px', sm: '17px', md: '20px' },
              lineHeight: { xs: 1.5, sm: 1.80, md: 1.3 },
              mb: { xs: 2, sm: 2, md: 1.5, lg: 1.5 },
              color: 'text.secondary',
              fontFamily: "'Roboto', sans-serif",
              letterSpacing: '0.5px',
            }}
          >
            is a Meta-approved business messaging partner offering a sector-agnostic SaaS platform. We automate workflows over WhatsApp to help businesses scale smarter operations-all within a no-code, intuitive environment.
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: '15px', sm: '17px', md: '20px' },
              lineHeight: { xs: 1.6, sm: 1.80, md: 1.3 },
              mb: { xs: 2, sm: 2, md: 1.5, lg: 1.5 },
              color: 'text.secondary',
              fontFamily: "'Roboto', sans-serif",
              letterSpacing: '0.5px',
            }}
          >
            With AI-powered tools, brands can automate conversations, deliver personalized experiences, and improve efficiency at scale-all from one unified platform.
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default Section7;